import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';
import {
  ServiceVisitType,
  NewServiceVisitType,
  UpdateServiceVisitType,
  ServiceVisitTypeFilters,
} from '../models';

const serviceVisitTypesService = createApi({
  reducerPath: 'serviceVisitTypesService',
  baseQuery: createBaseQueryWithReauth(
    `${API_BUSINESS_URL}/service-visit-types`
  ),
  tagTypes: ['ServiceVisitTypes', 'ServiceVisitType'],
  endpoints: (builder) => ({
    getServiceVisitTypes: builder.query<
      PaginatedResponse<ServiceVisitType>,
      { paginated: PaginatedRequest; filters?: ServiceVisitTypeFilters }
    >({
      query: ({ paginated, filters }) => ({
        url: '',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters?.name?.trim() && { name: filters.name.trim() }),
          ...(filters?.logisticOperatorId?.trim() && {
            logisticOperatorId: filters.logisticOperatorId.trim(),
          }),
          ...(filters?.type && { type: filters.type }),
        },
      }),
      providesTags: (result) => [
        ...(result?.items?.map(({ id }) => ({
          type: 'ServiceVisitType' as const,
          id,
        })) ?? []),
        'ServiceVisitTypes',
      ],
    }),
    getServiceVisitTypeById: builder.query<ServiceVisitType, { id: string }>({
      query: ({ id }) => ({ url: `/${id}` }),
      providesTags: (_result, _error, { id }) => [
        { type: 'ServiceVisitType' as const, id },
      ],
    }),
    createServiceVisitType: builder.mutation<
      ServiceVisitType,
      NewServiceVisitType
    >({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: ['ServiceVisitTypes'],
    }),
    updateServiceVisitType: builder.mutation<
      ServiceVisitType,
      { id: string; body: UpdateServiceVisitType }
    >({
      query: ({ id, body }) => ({ url: `/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [
        'ServiceVisitTypes',
        { type: 'ServiceVisitType' as const, id },
      ],
    }),
    deleteServiceVisitType: builder.mutation<void, string>({
      query: (id) => ({ url: `/${id}`, method: 'DELETE' }),
      invalidatesTags: ['ServiceVisitTypes'],
    }),
  }),
});

export default serviceVisitTypesService;
export const {
  useGetServiceVisitTypesQuery,
  useGetServiceVisitTypeByIdQuery,
  useCreateServiceVisitTypeMutation,
  useUpdateServiceVisitTypeMutation,
  useDeleteServiceVisitTypeMutation,
} = serviceVisitTypesService;
